import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { LanguageService } from '../../core/services/language.service';
import { ITextLocaleMap } from '../models/workflows.models';
import {
	WORKFLOW_DOC_INSTRUCTIONS_I18N_PATH,
	WORKFLOW_FORM_I18N_KEYS,
} from '../constants/workflow-form-defaults.constants';
import { buildLocaleMapFromTranslationBundles } from '../utils/workflow-form-defaults.util';
import { forkJoin, map, Observable, of, tap } from 'rxjs';

/**
 * Textos por defecto del formulario de workflow leídos de los mismos JSON que ngx-translate
 * (`/i18n/es.json`, `/i18n/en.json`). Debe ejecutarse `preload()` al arranque (ver `app.config`).
 */
@Injectable({
	providedIn: 'root',
})
export class WorkflowFormDefaultsService {
	private http = inject(HttpClient);
	private langSvc = inject(LanguageService);

	private bundlesByLocale: Record<string, unknown> = {};

	/** Precarga bundles de todos los idiomas permitidos antes de usar `getLocaleMapForKey`. */
	preload(): Observable<void> {
		const allowed = this.langSvc.getAllowedLanguages();
		if (allowed.length === 0) {
			return of(undefined);
		}
		const allLoaded = allowed.every((locale) => this.bundlesByLocale[locale] != null);
		if (allLoaded) {
			return of(undefined);
		}
		const requests: Record<string, Observable<unknown>> = {};
		for (const locale of allowed) {
			requests[locale] = this.http.get<unknown>(`/i18n/${locale}.json`);
		}
		return forkJoin(requests).pipe(
			tap((bundles) => {
				this.bundlesByLocale = bundles;
			}),
			map(() => undefined),
		);
	}

	private ensureLoaded(): void {
		const allowed = this.langSvc.getAllowedLanguages();
		const allLoaded = allowed.length > 0 && allowed.every((locale) => this.bundlesByLocale[locale] != null);
		if (!allLoaded) {
			throw new Error(
				'WorkflowFormDefaultsService: ejecutar preload() en el inicializador de la aplicación antes de leer los textos por defecto.',
			);
		}
	}

	/** Mapa por idioma para una clave i18n (puntos) presente en los JSON de idiomas permitidos. */
	getLocaleMapForKey(i18nPath: string): ITextLocaleMap {
		this.ensureLoaded();
		const allowed = this.langSvc.getAllowedLanguages();
		return buildLocaleMapFromTranslationBundles(allowed, i18nPath, this.bundlesByLocale);
	}

	getHomeFormDescriptionDefaults(): ITextLocaleMap {
		return this.getLocaleMapForKey(WORKFLOW_FORM_I18N_KEYS.HOME_FORM_DESCRIPTION);
	}

	getFinishLoadingTextDefaults(): ITextLocaleMap {
		return this.getLocaleMapForKey(WORKFLOW_FORM_I18N_KEYS.FINISH_LOADING_TEXT);
	}

	getGenericDocTitleDefaults(): ITextLocaleMap {
		return this.getLocaleMapForKey(WORKFLOW_FORM_I18N_KEYS.GENERIC_DOC_TITLE);
	}

	getGenericDocDescriptionDefaults(): ITextLocaleMap {
		return this.getLocaleMapForKey(WORKFLOW_FORM_I18N_KEYS.GENERIC_DOC_DESCRIPTION);
	}

	getDelayedProcessDefaultWarningTextDefaults(): ITextLocaleMap {
		return this.getLocaleMapForKey(WORKFLOW_FORM_I18N_KEYS.DELAYED_PROCESS_DEFAULT_WARNING_TEXT);
	}

	getDocumentsSectionTitleDefaults(): ITextLocaleMap {
		return this.getLocaleMapForKey(WORKFLOW_FORM_I18N_KEYS.DOCUMENTS_SECTION_TITLE);
	}

	/** Pautas por tipo de documento o conexión (mismas claves que en el alta / `DOCS.*_INSTRUCTIONS`). */
	getDocInstructionsDefaults(docType: string): ITextLocaleMap {
		this.ensureLoaded();
		const allowed = this.langSvc.getAllowedLanguages();
		const path = WORKFLOW_DOC_INSTRUCTIONS_I18N_PATH[docType];
		if (!path) {
			return Object.fromEntries(allowed.map((l) => [l, ''])) as ITextLocaleMap;
		}
		return this.getLocaleMapForKey(path);
	}

	/** Título corto por tipo (bloque `DOCS` o evaluación financiera para conexión bancaria). */
	getDocTitleDefaults(docType: string): ITextLocaleMap {
		if (docType === 'BANK_SCORING') {
			return this.getLocaleMapForKey('FINANCIAL_EVALUATION.BANK_SCORING');
		}
		return this.getLocaleMapForKey(`DOCS.${docType}`);
	}

	/** Descripción por tipo; sin entrada dedicada devuelve mapa vacío por idioma. */
	getDocDescriptionDefaults(docType: string): ITextLocaleMap {
		this.ensureLoaded();
		const allowed = this.langSvc.getAllowedLanguages();
		const empty = (): ITextLocaleMap => Object.fromEntries(allowed.map((l) => [l, ''])) as ITextLocaleMap;
		if (docType === 'BANK_SCORING') {
			return empty();
		}
		return this.getLocaleMapForKey(`DOCS.${docType}_DESCRIPTION`);
	}
}
